import React, { FunctionComponent, useContext, useState } from "react";
import { getDefaultStyleProperty } from "@iventis/map-engine/src/utilities/style-helpers";
import { Content } from "@iventis/translations/content/typed-content";
import { AreaModelStyle } from "@iventis/domain-model/model/areaModelStyle";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { TitleWithIcon } from "@iventis/styles";
import { AssetType } from "@iventis/domain-model/model/assetType";
import {
    ColourSelectorFormulaPreview,
    LoadingComponent,
    colourSelectorComponentCreator,
    definedValueSelectorComponentCreator,
    sliderWithTextSelectorCreator,
} from "@iventis/components";
import { createStaticStyleValue } from "@iventis/layer-style-helpers";
import { StyleValue } from "@iventis/domain-model/model/styleValue";
import { useAssetSignatureCache } from "@iventis/utilities";
import { StyleValueExtractionMethod } from "@iventis/domain-model/model/styleValueExtractionMethod";
import { AreaModelRotation } from "@iventis/domain-model/model/areaModelRotation";
import { AreaModelDistribution } from "@iventis/domain-model/model/areaModelDistribution";
import { EditStyleItemComponent } from "./edit-style-item";
import { EditStyleProps } from "./style.types";
import StyleContainer from "./style-container";
import { StyledStyleItemContainer } from "./line-edit-style";
import { useModelSelector } from "./edit-style.helpers";
import { EditStyleContext } from "./edit-style-context";
import { ModelAttributeBasedModalStatus, ModelAttributeStyles } from "./model-attribute-styles";

export const AreaModelEditStyleComponent: FunctionComponent<EditStyleProps<AreaModelStyle>> = ({ layerStyle, changeStyleValue, dataFields }) => {
    const translate = useIventisTranslate();

    const imageGetter = useAssetSignatureCache();

    const { assetService, getModels, modelGetter, onAssetRefresh } = useContext(EditStyleContext);

    const isModelStyledByAttributes = layerStyle.model.extractionMethod === StyleValueExtractionMethod.Mapped;

    const [attributeBasedModal, setAttributeBasedModal] = useState<ModelAttributeBasedModalStatus>("hidden");

    const { selectedModels, thumbnailAssetIds, modelSelector, changeModelThumbnail } = useModelSelector(
        layerStyle,
        (id) => {
            changeStyleValue("model", createStaticStyleValue(id));
        },
        getModels,
        dataFields,
        () => handleRemoveAttributeBasedStyling(true),
        () => setAttributeBasedModal("model-only")
    );

    const handleRemoveAttributeBasedStyling = (model: boolean) => {
        if (model) {
            changeStyleValue("model", { ...layerStyle.model, mappedValues: {}, dataFieldId: null, extractionMethod: StyleValueExtractionMethod.Static });
        }
    };

    const getModel = async (modelId: string) => {
        const models = await getModels();
        const model = models.find((model) => modelId === model.thumbnailAssetId);
        const modelFromGetter = await modelGetter([model.id]);
        return modelFromGetter[0];
    };

    const handleSetAttributeBasedModel = (styleValue: StyleValue<string>) => {
        changeStyleValue("model", styleValue);
        setAttributeBasedModal("hidden");
    };

    return (
        <>
            <StyleContainer title={<TitleWithIcon title={translate(Content.map2.styles2.model)} icon={["fas", "cube"]} />} testId="model">
                <StyledStyleItemContainer>
                    {selectedModels != null ? (
                        <EditStyleItemComponent
                            styleProperty="model"
                            // For now, we must create a dummy static value until we introduce data driven styling to models
                            value={createStaticStyleValue(selectedModels[0].thumbnailAssetId)}
                            changeStyleValue={changeModelThumbnail}
                            component={modelSelector({
                                assetType: AssetType.Model,
                                selectorDescription: translate(Content.map2.styles2.choose_model),
                                allowedAssetIds: thumbnailAssetIds,
                                imageUrlGetter: (asset) => imageGetter(asset.assetUrl, asset.authoritySignature),
                                assetService,
                                getModel,
                                onAssetRefresh,
                            })}
                            title={translate(Content.map2.styles2.type)}
                            dataFields={dataFields}
                            showDataDriven={!isModelStyledByAttributes}
                            onShowFormula={() => setAttributeBasedModal("model-only")}
                        />
                    ) : (
                        <LoadingComponent />
                    )}
                </StyledStyleItemContainer>
                <StyledStyleItemContainer>
                    {/* DISTRIBUTION TYPE */}
                    <EditStyleItemComponent
                        styleProperty="distributionType"
                        value={layerStyle.distributionType ?? getDefaultStyleProperty(layerStyle.styleType, "distributionType")}
                        changeStyleValue={changeStyleValue}
                        component={definedValueSelectorComponentCreator<AreaModelDistribution>({
                            optionalValues: [
                                { value: AreaModelDistribution.Random, label: translate(Content.map10.areaModel.distribution.random), testId: "distribution-random" },
                                { value: AreaModelDistribution.Uniform, label: translate(Content.map10.areaModel.distribution.uniform), testId: "distribution-uniform" },
                            ],
                        })}
                        title={translate(Content.map10.areaModel.distribution.title)}
                    />
                    {/* ROTATION TYPE */}
                    <EditStyleItemComponent
                        styleProperty="rotationType"
                        value={layerStyle.rotationType ?? getDefaultStyleProperty(layerStyle.styleType, "rotationType")}
                        changeStyleValue={changeStyleValue}
                        component={definedValueSelectorComponentCreator<AreaModelRotation>({
                            optionalValues: [
                                { value: AreaModelRotation.Random, label: translate(Content.map10.areaModel.rotation.random), testId: "rotation-random" },
                                { value: AreaModelRotation.Uniform, label: translate(Content.map10.areaModel.rotation.uniform), testId: "rotation-uniform" },
                            ],
                        })}
                        title={translate(Content.map10.areaModel.rotation.title)}
                    />
                </StyledStyleItemContainer>
            </StyleContainer>
            <StyleContainer title={<TitleWithIcon title={translate(Content.map2.styles2.Area)} icon={["fas", "diamond"]} />} testId="area">
                <StyledStyleItemContainer>
                    {/* FILL COLOUR */}
                    <EditStyleItemComponent
                        styleProperty="areaColour"
                        component={colourSelectorComponentCreator()}
                        PreviewComponent={ColourSelectorFormulaPreview}
                        title={translate(Content.map2.styles.colour)}
                        value={layerStyle.areaColour || getDefaultStyleProperty(layerStyle.styleType, "areaColour")}
                        changeStyleValue={changeStyleValue}
                        dataFields={dataFields}
                    />
                    {/* OPACITY */}
                    <EditStyleItemComponent
                        styleProperty="areaOpacity"
                        value={layerStyle.areaOpacity || getDefaultStyleProperty(layerStyle.styleType, "areaOpacity")}
                        component={sliderWithTextSelectorCreator({
                            minValue: 0,
                            maxValue: 1,
                            increment: 0.05,
                            colour: layerStyle.areaColour.staticValue.staticValue,
                            testId: "opacity-selector",
                        })}
                        changeStyleValue={changeStyleValue}
                        title={translate(Content.map2.styles.opacity)}
                        isZoomableValue={false}
                    />
                </StyledStyleItemContainer>
            </StyleContainer>
            {attributeBasedModal !== "hidden" && selectedModels != null && (
                <ModelAttributeStyles
                    dataFields={dataFields}
                    onClose={() => setAttributeBasedModal("hidden")}
                    onConfirm={handleSetAttributeBasedModel}
                    style={layerStyle}
                    selectedDataFieldId={layerStyle?.model?.dataFieldId}
                    type="model-only"
                    modelsWithThumbnails={selectedModels}
                    getModel={getModel}
                />
            )}
        </>
    );
};
